// src/components/battle/BattleFilterBar.jsx
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";

const STATUS_OPTIONS = [
  { label: "전체 상태", value: "" },
  { label: "모집중", value: "0" },
  { label: "모집완료", value: "1" },
  { label: "종료", value: "2" },
];

const MODE_OPTIONS = [
  { label: "모드 전체", value: "" },
  { label: "개인전", value: "0" },
  { label: "팀전", value: "1" },
];

const GENDER_OPTIONS = [
  { label: "성별 전체", value: "" },
  { label: "혼성전", value: "0" },
  { label: "여자전", value: "1" },
  { label: "남자전", value: "2" },
];

const ACCENT = "rgb(0, 224, 198)";

// ✅ 전부 흰색 버튼 기본(비활성)
// ✅ 활성만 민트 "라인+연한배경" 포인트 (꽉찬 민트 버튼 없음)
const chipBase =
  "whitespace-nowrap text-[11px] px-3 py-1 rounded-full border border-slate-200 bg-white text-slate-600";
const chipActive = "font-semibold";

// ✅ 공통 인풋 스타일 (코스선택/생성 인풋과 동일 톤)
const WS_INPUT =
  "w-full rounded-xl border border-slate-200 bg-gray-50 px-4 py-3 text-xs " +
  "outline-none placeholder:text-slate-400 " +
  "focus:border-[rgb(0,224,198)] focus:ring-2 focus:ring-[rgb(0,224,198)]/20";

export default function BattleFilterBar({ filters, onChange }) {
  const [localKeyword, setLocalKeyword] = useState(filters.searchKeyword || "");
  const [openSheet, setOpenSheet] = useState(false);

  const update = (patch) => onChange({ ...filters, ...patch });

  const [portalEl, setPortalEl] = useState(null);

  useEffect(() => {
    // SSR 아니고 브라우저에서만
    setPortalEl(document.body);
  }, []);

  // ✅ 모바일 웹앱: 바텀시트 열릴 때 뒤 스크롤 잠금
  useEffect(() => {
    if (!openSheet) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [openSheet]);

  const handleSearch = () => {
    const keyword = (localKeyword || "").trim();
    const isDateLike = /^\d{4}[-./]\d{2}[-./]\d{2}$/.test(keyword);

    if (isDateLike) {
      const normalized = keyword.replace(/[./]/g, "-");
      update({
        searchKeyword: "",
        searchStartDate: normalized,
      });
    } else {
      update({
        searchKeyword: keyword,
        searchStartDate: filters.searchStartDate || "",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleDateChange = (e) => {
    const value = e.target.value; // YYYY-MM-DD
    update({
      searchStartDate: value,
      searchKeyword: filters.searchKeyword || localKeyword || "",
    });
  };

  const handleReset = () => {
    setLocalKeyword("");
    update({
      searchKeyword: "",
      searchStartDate: "",
      modeFilter: "",
      statusFilter: "",
      genderType: "",
    });
  };

  const currentStatus = STATUS_OPTIONS.find(
    (o) => (filters.statusFilter ?? "") === o.value
  )?.label;
  const currentMode = MODE_OPTIONS.find(
    (o) => (filters.modeFilter ?? "") === o.value
  )?.label;
  const currentGender = GENDER_OPTIONS.find(
    (o) => (filters.genderType ?? "") === o.value
  )?.label;

  // ✅ Portal로 띄울 바텀시트 (디자인/기능 동일 + 위치만 안정화)
  const sheetUI = (
    <div
      className="fixed inset-0 bg-slate-900/40 flex justify-center items-end z-[9999] overscroll-none"
      onClick={() => setOpenSheet(false)}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-2xl p-4 max-h-[70vh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-semibold">필터 설정</span>
          <button
            className="text-lg leading-none"
            type="button"
            onClick={() => setOpenSheet(false)}
          >
            ✕
          </button>
        </div>

        {/* 시작일 */}
        <div className="mt-2">
          <div className="text-xs font-semibold mb-2">시작일</div>
          <input
            type="date"
            className={WS_INPUT}
            value={filters.searchStartDate || ""}
            onChange={handleDateChange}
          />
        </div>

        {/* 상태 / 모드 / 성별 */}
        <div className="mt-3">
          <div className="text-xs font-semibold mb-1">대결 상태</div>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((item) => {
              const isActive = (filters.statusFilter ?? "") === item.value;
              return (
                <button
                  key={`S2_${item.value || "ALL"}`}
                  type="button"
                  onClick={() => update({ statusFilter: item.value })}
                  className={chipBase + " " + (isActive ? chipActive : "")}
                  style={
                    isActive
                      ? {
                          borderColor: ACCENT,
                          backgroundColor: "rgba(0, 224, 198, 0.12)",
                          color: ACCENT,
                        }
                      : undefined
                  }
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3">
          <div className="text-xs font-semibold mb-1">모드</div>
          <div className="flex flex-wrap gap-2">
            {MODE_OPTIONS.map((item) => {
              const isActive = (filters.modeFilter ?? "") === item.value;
              return (
                <button
                  key={`M2_${item.value || "ALL"}`}
                  type="button"
                  onClick={() => update({ modeFilter: item.value })}
                  className={chipBase + " " + (isActive ? chipActive : "")}
                  style={
                    isActive
                      ? {
                          borderColor: ACCENT,
                          backgroundColor: "rgba(0, 224, 198, 0.12)",
                          color: ACCENT,
                        }
                      : undefined
                  }
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3">
          <div className="text-xs font-semibold mb-1">성별</div>
          <div className="flex flex-wrap gap-2">
            {GENDER_OPTIONS.map((item) => {
              const isActive = (filters.genderType ?? "") === item.value;
              return (
                <button
                  key={`G2_${item.value || "ALL"}`}
                  type="button"
                  onClick={() => update({ genderType: item.value })}
                  className={chipBase + " " + (isActive ? chipActive : "")}
                  style={
                    isActive
                      ? {
                          borderColor: ACCENT,
                          backgroundColor: "rgba(0, 224, 198, 0.12)",
                          color: ACCENT,
                        }
                      : undefined
                  }
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 요약 */}
        <div className="mt-3 px-3 py-2 rounded-xl bg-slate-50 text-[11px] text-slate-600 flex flex-wrap gap-1.5">
          <span>{currentStatus}</span>
          <span>{currentMode}</span>
          <span>{currentGender}</span>
        </div>

        {/* 버튼 */}
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-xs"
            onClick={handleReset}
          >
            초기화
          </button>
          <button
            type="button"
            className="flex-1 rounded-xl text-white text-xs font-semibold px-4 py-3"
            style={{ backgroundColor: ACCENT }}
            onClick={() => setOpenSheet(false)}
          >
            적용하기
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <section className="flex flex-col gap-2 mb-2 px-4">
      {/* 검색줄 */}
      <div className="flex gap-2">
        <input
          type="text"
          className={`flex-1 ${WS_INPUT}`}
          placeholder="대결명 (예: 한강 5km 나이트런)"
          value={localKeyword}
          onChange={(e) => setLocalKeyword(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        <button
          type="button"
          className="rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white"
          onClick={handleSearch}
        >
          <Search className="w-4 h-4 text-slate-600" strokeWidth={2.2} />
        </button>
      </div>

      {/* 슬라이더 + 필터 버튼 */}
      <div className="flex items-center gap-2">
        <div
          className="
            flex-1 flex gap-2 overflow-x-auto pb-1
            [scrollbar-width:none]
            [-ms-overflow-style:none]
            [&::-webkit-scrollbar]:hidden
          "
        >
          {/* ✅ 대결 상태 */}
          {STATUS_OPTIONS.map((item) => {
            const isActive = (filters.statusFilter ?? "") === item.value;

            return (
              <button
                key={`S_${item.value || "ALL"}`}
                type="button"
                onClick={() => update({ statusFilter: item.value })}
                className={chipBase + " " + (isActive ? chipActive : "")}
                style={
                  isActive
                    ? {
                        borderColor: ACCENT,
                        backgroundColor: "rgba(0,224,198,0.12)",
                        color: ACCENT,
                      }
                    : undefined
                }
              >
                {item.label}
              </button>
            );
          })}

          {/* ✅ 모드 */}
          {MODE_OPTIONS.map((item) => {
            const isActive = (filters.modeFilter ?? "") === item.value;

            return (
              <button
                key={`M_${item.value || "ALL"}`}
                type="button"
                onClick={() => update({ modeFilter: item.value })}
                className={chipBase + " " + (isActive ? chipActive : "")}
                style={
                  isActive
                    ? {
                        borderColor: ACCENT,
                        backgroundColor: "rgba(0,224,198,0.12)",
                        color: ACCENT,
                      }
                    : undefined
                }
              >
                {item.label}
              </button>
            );
          })}

          {/* ✅ 성별 */}
          {GENDER_OPTIONS.map((item) => {
            const isActive = (filters.genderType ?? "") === item.value;

            return (
              <button
                key={`G_${item.value || "ALL"}`}
                type="button"
                onClick={() => update({ genderType: item.value })}
                className={chipBase + " " + (isActive ? chipActive : "")}
                style={
                  isActive
                    ? {
                        borderColor: ACCENT,
                        backgroundColor: "rgba(0,224,198,0.12)",
                        color: ACCENT,
                      }
                    : undefined
                }
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className={`${chipBase} flex items-center gap-0.5 text-[11px]`}
          onClick={() => setOpenSheet(true)}
        >
          필터 <span className="text-xs">⌄</span>
        </button>
      </div>

      {/* ✅ 바텀시트: Portal로 body에 렌더링 */}
      {openSheet && portalEl ? createPortal(sheetUI, portalEl) : null}
    </section>
  );
}
